import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../supabaseClient'
import { Upload, CheckCircle2, AlertCircle, Loader2, ArrowRight, ArrowLeft, Send, FileText, Stethoscope, Check, MessageSquare, Activity, MapPin, Mail, Phone, Calendar, ChevronDown, User, Hash, ClipboardList, X, Plus, CheckCircle, Info } from 'lucide-react'
import { useNotifications } from '../context/NotificationContext'
import './PatientPortal.css'

const getLocalISODate = (date) => {
  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - (offset * 60 * 1000))
  return localDate.toISOString().split('T')[0]
}

const parseFullName = (fullName) => {
  if (!fullName) return { firstname: '', lastname: '' }
  const trimmed = fullName.trim()
  if (trimmed.includes(',')) {
    const commaParts = trimmed.split(',')
    return {
      lastname: commaParts[0].trim(),
      firstname: commaParts.slice(1).join(',').trim()
    }
  }
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) {
    return { firstname: '', lastname: trimmed }
  }
  return {
    lastname: parts[0],
    firstname: parts.slice(1).join(' ')
  }
}

const resolveProfessionalForService = (serviceName, currentProfessionals, currentServices) => {
  if (!serviceName) return null
  const nameLower = serviceName.toLowerCase()

  let matchedProf = null

  // Primero intentamos hacer match mediante la asignación de base de datos (array services)
  const serviceObj = currentServices.find(s => s.name.toLowerCase() === nameLower)
  if (serviceObj) {
    matchedProf = currentProfessionals.find(p => p.services?.includes(serviceObj.id))
  }

  if (!matchedProf) {
    matchedProf = currentProfessionals.find(p => p.name.toUpperCase().includes('CONSULTORIOS EXTERNOS')) 
               || currentProfessionals.find(p => p.name.toUpperCase().includes('ADMINISTRACION'))
  }

  if (!matchedProf && currentProfessionals.length > 0) {
    matchedProf = currentProfessionals[0]
  }

  return matchedProf
}

const PatientPortal = () => {
  const { addNotification } = useNotifications()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [busySlots, setBusySlots] = useState([])
  const [services, setServices] = useState([])
  const [isInsuranceOpen, setIsInsuranceOpen] = useState(false)
  const [isCountryOpen, setIsCountryOpen] = useState(false)
  const [professionals, setProfessionals] = useState([])
  const [selectedProfessional, setSelectedProfessional] = useState(null)
  const [isSlotsLoading, setIsSlotsLoading] = useState(false)
  const [submittedData, setSubmittedData] = useState(null)
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [isValidatingDni, setIsValidatingDni] = useState(false)
  const [holidays, setHolidays] = useState([])

  // Dynamic portal settings states
  const [portalSettings, setPortalSettings] = useState(null)
  const [isPortalSettingsLoading, setIsPortalSettingsLoading] = useState(true)
  const [cart, setCart] = useState([])
  const [selectedSpecialty, setSelectedSpecialty] = useState('ALL')

  useEffect(() => {
    fetchPortalSettings()
    fetchServices()
    fetchProfessionals()
    fetchBranches()
    fetchHolidays()
  }, [])

  const fetchPortalSettings = useCallback(async () => {
    try {
      setIsPortalSettingsLoading(true)
      const { data, error } = await supabase.from('system_settings').select('*')
      if (error) throw error
      
      if (data) {
        const settingsMap = {}
        data.forEach(item => {
          settingsMap[item.key] = item.value
        })
        setPortalSettings(settingsMap)
        
        // Dynamically apply primary color theme if configured
        if (settingsMap.portal_primary_color) {
          document.documentElement.style.setProperty('--primary-cyan', settingsMap.portal_primary_color)
        }
      } else {
        setPortalSettings({})
      }
    } catch (err) {
      console.error('Error fetching portal settings:', err)
      setPortalSettings({})
    } finally {
      setIsPortalSettingsLoading(false)
    }
  }, [])

  const availableSpecialties = useMemo(() => {
    const specs = new Set()
    professionals.forEach(p => {
      if (p.specialty) specs.add(p.specialty)
    })
    return Array.from(specs).sort()
  }, [professionals])

  const filteredServicesList = useMemo(() => {
    if (!portalSettings || portalSettings.portal_show_specialty !== 'true' || selectedSpecialty === 'ALL') {
      return services
    }
    const allowedIds = professionals
      .filter(p => p.specialty === selectedSpecialty)
      .flatMap(p => p.services || [])
      
    return services.filter(s => allowedIds.includes(s.id))
  }, [services, professionals, selectedSpecialty, portalSettings])

  const fetchHolidays = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('holidays').select('*')
      if (error) throw error
      if (data) setHolidays(data)
    } catch (err) {
      console.error('Error fetching holidays:', err)
    }
  }, [])

  const getDayBlockReason = (dateStr, professionalName) => {
    if (!dateStr) return null

    // Check portal settings global holidays
    if (portalSettings && portalSettings.global_holidays) {
      let gh = [];
      try { gh = typeof portalSettings.global_holidays === 'string' ? JSON.parse(portalSettings.global_holidays) : portalSettings.global_holidays; } catch(e) {}
      if ((Array.isArray(gh) && gh.includes(dateStr))) return 'Feriado Institucional / Día no laborable';
    }

    const generalBlock = holidays.find(h => h.holiday_date === dateStr && (h.scope === 'general' || !h.scope))
    if (generalBlock) return generalBlock.description || 'Feriado General / Día no laborable'
    
    if (professionalName) {
      const profBlock = holidays.find(h => h.holiday_date === dateStr && h.scope === 'professional' && h.professional_name === professionalName)
      if (profBlock) return profBlock.description || `Bloqueo de Agenda (${professionalName})`
      
      const prof = professionals.find(p => p.name === professionalName);
      if (prof && prof.working_hours && prof.working_hours.off_dates && (Array.isArray(prof.working_hours.off_dates) && prof.working_hours.off_dates.includes(dateStr))) {
        return 'Vacaciones / Día Libre del Profesional';
      }
    }
    
    return null
  }


  const fetchBranches = useCallback(async () => {
    const { data, error } = await supabase.from('branches').select('*').order('name', { ascending: true })
    if (error || !data || data.length === 0) {
      setBranches([
        { id: 1, name: 'Belgrano', address: 'Av. Cabildo 2400', phone: '11-4788-9900' },
        { id: 2, name: 'Nuñez', address: 'Av. del Libertador 6500', phone: '11-4702-3300' },
        { id: 3, name: 'Ramos Mejía', address: 'Av. de Mayo 300', phone: '11-4654-1100' },
        { id: 4, name: 'Caballito', address: 'Av. Rivadavia 5000', phone: '11-4901-2200' }
      ])
    } else {
      setBranches(data)
    }
  }, [])

  const fetchServices = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('services').select('*').order('name');
      if (error) throw error;
      if (data && data.length > 0) {
        setServices(data);
      } else {
        setServices([
          { id: 1, name: 'Consulta Médica General', duration: 20, price: 5000, requirements: 'DNI y Carnet de Obra Social' },
          { id: 2, name: 'Consulta Especializada', duration: 30, price: 8500, requirements: 'DNI y Carnet de Obra Social' },
          { id: 3, name: 'Ecografía General / Control', duration: 15, price: 7200, requirements: 'Orden Médica, Ayuno 8hs' },
          { id: 4, name: 'Radiografía Digital', duration: 10, price: 4500, requirements: 'Orden Médica' },
          { id: 5, name: 'Sesión de Fonoaudiología', duration: 40, price: 6000, requirements: 'Orden Médica' },
          { id: 6, name: 'Consulta Psicológica', duration: 45, price: 7000, requirements: 'DNI' }
        ]);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setServices([
        { id: 1, name: 'Consulta Médica General', duration: 20, price: 5000, requirements: 'DNI y Carnet de Obra Social' },
        { id: 2, name: 'Consulta Especializada', duration: 30, price: 8500, requirements: 'DNI y Carnet de Obra Social' },
        { id: 3, name: 'Ecografía General / Control', duration: 15, price: 7200, requirements: 'Orden Médica, Ayuno 8hs' },
        { id: 4, name: 'Radiografía Digital', duration: 10, price: 4500, requirements: 'Orden Médica' },
        { id: 5, name: 'Sesión de Fonoaudiología', duration: 40, price: 6000, requirements: 'Orden Médica' },
        { id: 6, name: 'Consulta Psicológica', duration: 45, price: 7000, requirements: 'DNI' }
      ]);
    }
  }, [])

  const fetchProfessionals = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*');
      if (error) throw error;
      if (data && data.length > 0) {
        const mapped = data.map(p => ({
          id: p.id,
          name: p.full_name,
          specialty: p.specialty,
          services: p.services || [],
          working_hours: p.working_hours || null
        }));
        setProfessionals(mapped);
      } else {
        setProfessionals([
          { id: 2, name: 'CONSULTORIOS EXTERNOS', specialty: 'Consultorio General', services: [1, 2, 3, 4, 5, 6] },
          { id: 3, name: 'ECOGRAFIAS .', specialty: 'Diagnóstico por Imágenes', services: [3, 6] },
          { id: 4, name: 'FONOAUDIOLOGA .', specialty: 'Fonoaudiología', services: [1, 2, 3, 4, 5, 6] },
          { id: 5, name: 'NEUMONOLOGO .', specialty: 'Neumonología', services: [1, 2, 3, 4, 5, 6] },
          { id: 6, name: 'NEUROLOGO .', specialty: 'Neurología', services: [1, 2, 3, 4, 5, 6] },
          { id: 7, name: 'OTORRINO .', specialty: 'Otorrinolaringología', services: [1, 2, 3, 4, 5, 6] },
          { id: 8, name: 'PSICOLOGIA .', specialty: 'Psicología Clínica', services: [1, 2, 3, 4, 5, 6] },
          { id: 9, name: 'RAYOS X', specialty: 'Radiología Digital', services: [4] },
          { id: 1, name: 'ADMINISTRACION GDT', specialty: 'Gestión y Trámites', services: [1, 2, 3, 4, 5, 6] }
        ]);
      }
    } catch (err) {
      console.error('Error fetching professionals:', err);
      setProfessionals([
        { id: 2, name: 'CONSULTORIOS EXTERNOS', specialty: 'Consultorio General', services: [1, 2, 3, 4, 5, 6] },
        { id: 3, name: 'ECOGRAFIAS .', specialty: 'Diagnóstico por Imágenes', services: [3, 6] },
        { id: 4, name: 'FONOAUDIOLOGA .', specialty: 'Fonoaudiología', services: [1, 2, 3, 4, 5, 6] },
        { id: 5, name: 'NEUMONOLOGO .', specialty: 'Neumonología', services: [1, 2, 3, 4, 5, 6] },
        { id: 6, name: 'NEUROLOGO .', specialty: 'Neurología', services: [1, 2, 3, 4, 5, 6] },
        { id: 7, name: 'OTORRINO .', specialty: 'Otorrinolaringología', services: [1, 2, 3, 4, 5, 6] },
        { id: 8, name: 'PSICOLOGIA .', specialty: 'Psicología Clínica', services: [1, 2, 3, 4, 5, 6] },
        { id: 9, name: 'RAYOS X', specialty: 'Radiología Digital', services: [4] },
        { id: 1, name: 'ADMINISTRACION GDT', specialty: 'Gestión y Trámites', services: [1, 2, 3, 4, 5, 6] }
      ]);
    }
  }, [])

  const updateProfessionalForServices = (selectedServiceNames, currentProfessionals, currentServices) => {
    if (!selectedServiceNames || selectedServiceNames.length === 0) {
      setSelectedProfessional(null);
      return;
    }
    const matchedProf = resolveProfessionalForService(selectedServiceNames[0], currentProfessionals, currentServices);
    if (matchedProf) {
      setSelectedProfessional(matchedProf);
    }
  };


  const fetchBusySlots = useCallback(async (date, profName) => {
    if (!date || !profName) return
    setIsSlotsLoading(true)
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('appointment_time, professional_name, status')
        .eq('appointment_date', date)
        .neq('status', 'cancelado')
      
      if (error) throw error
      
      if (data) {
        const appsForTime = data
          .filter(app => app.professional_name === profName || app.professional_name === 'Clínica General')
          .map(app => app.appointment_time?.substring(0, 5))
          .filter(Boolean)
        
        const timeCounts = {};
        appsForTime.forEach(time => {
          timeCounts[time] = (timeCounts[time] || 0) + 1;
        });

        // Get max_overbooking configuration
        const prof = professionals.find(p => p.name === profName);
        const maxOverbooking = prof?.working_hours?.max_overbooking || 0;
        const maxAllowed = 1 + parseInt(maxOverbooking, 10);
        
        const busyTimes = Object.keys(timeCounts).filter(time => timeCounts[time] >= maxAllowed);
        
        setBusySlots(busyTimes)
      }
    } catch (err) {
      console.error('Error fetching availability:', err)
    } finally {
      setIsSlotsLoading(false)
    }
  }, [])

  const generateTimeSlots = () => {
    const slots = []
    const now = new Date()
    const appointmentDate = watch('appointment_date')
    const selectedProfName = selectedProfessional?.name
    const isToday = appointmentDate === getLocalISODate(now)
    
    let minHoursLimit = portalSettings && portalSettings.portal_min_time_before 
      ? parseInt(portalSettings.portal_min_time_before, 10) 
      : 2

    let slotDuration = 30;
    let shift1 = { start: null, end: null };
    let shift2 = { start: null, end: null };

    if (selectedProfName && appointmentDate) {
      const prof = professionals.find(p => p.name === selectedProfName);
      if (prof && prof.working_hours) {
        if (prof.working_hours.slot_duration) {
          slotDuration = parseInt(prof.working_hours.slot_duration, 10);
        }
        
        // Primero verificamos si hay una "fecha especifica" configurada
        if (prof.working_hours.specific_dates && prof.working_hours.specific_dates[appointmentDate]) {
          const specConfig = prof.working_hours.specific_dates[appointmentDate];
          if (specConfig.start && specConfig.end) {
            const [sh, sm] = specConfig.start.split(':').map(Number);
            const [eh, em] = specConfig.end.split(':').map(Number);
            shift1.start = sh * 60 + sm;
            shift1.end = eh * 60 + em;
          }
          if (specConfig.has_shift2 && specConfig.start2 && specConfig.end2) {
            const [sh, sm] = specConfig.start2.split(':').map(Number);
            const [eh, em] = specConfig.end2.split(':').map(Number);
            shift2.start = sh * 60 + sm;
            shift2.end = eh * 60 + em;
          }
        } else {
          // Si no hay fecha especifica, usamos la logica de dias de la semana
          const d = new Date(appointmentDate + 'T12:00:00'); // Use noon to avoid timezone shift
          const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
          const dayKey = days[d.getDay()];
          
          const dayConfig = prof.working_hours[dayKey];
          if (dayConfig && dayConfig.enabled) {
            if (dayConfig.start && dayConfig.end) {
              const [sh, sm] = dayConfig.start.split(':').map(Number);
              const [eh, em] = dayConfig.end.split(':').map(Number);
              shift1.start = sh * 60 + sm;
              shift1.end = eh * 60 + em;
            }
            if (dayConfig.has_shift2 && dayConfig.start2 && dayConfig.end2) {
              const [sh, sm] = dayConfig.start2.split(':').map(Number);
              const [eh, em] = dayConfig.end2.split(':').map(Number);
              shift2.start = sh * 60 + sm;
              shift2.end = eh * 60 + em;
            }
          } else {
            // Si el día está deshabilitado o no existe configuración para él, no hay turnos
            return [];
          }
        }
      }
    }
      
    // Si el profesional no tiene ninguna configuración o el día está desactivado
    if (shift1.start === null) {
      return [];
    }

    // Buscamos los límites absolutos para iterar
    const globalStart = shift1.start;
    const globalEnd = shift2.end !== null ? Math.max(shift1.end, shift2.end) : shift1.end;

    let currentTotalMinutes = globalStart;

    while (currentTotalMinutes <= globalEnd) {
      // Verificar si el currentTotalMinutes cae dentro del turno 1 o el turno 2
      const inShift1 = currentTotalMinutes >= shift1.start && currentTotalMinutes <= shift1.end;
      const inShift2 = shift2.start !== null && currentTotalMinutes >= shift2.start && currentTotalMinutes <= shift2.end;

      if (inShift1 || inShift2) {
        const h = Math.floor(currentTotalMinutes / 60);
        const m = currentTotalMinutes % 60;
        const t = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        
        if (isToday) {
          const slotDateTime = new Date(now)
          slotDateTime.setHours(h, m, 0, 0)
          
          const limitTime = new Date(now.getTime() + minHoursLimit * 60 * 60 * 1000)
          if (slotDateTime > limitTime) {
            slots.push(t)
          }
        } else {
          slots.push(t)
        }
      }
      
      currentTotalMinutes += slotDuration;
    }
    
    return slots
  }

  const { register, handleSubmit, watch, formState: { errors }, trigger, setValue, getValues } = useForm({
    defaultValues: {
      study_types: [],
      status: 'pendiente',
      appointment_date: getLocalISODate(new Date()),
      patient_dni: '',
      patient_firstname: '',
      patient_lastname: '',
      patient_birthdate: '',
      patient_weight: '',
      patient_email: '',
      patient_email_confirm: '',
      is_third_party: 'false',
      requester_name: '',
      requester_phone: '',
      country_code: '+54',
      patient_phone: '',
      reason: '',
      insurance_provider: '',
      insurance_plan: '',
      affiliate_number: '',
      address: '',
      city: '',
      province: '',
      country: 'Argentina'
    }
  })

  const selectedDate = watch('appointment_date')
  const blockReason = getDayBlockReason(selectedDate, selectedProfessional?.name)

  useEffect(() => {
    const appointmentDate = watch('appointment_date')
    if (appointmentDate) {
      const blockReason = getDayBlockReason(appointmentDate, selectedProfessional?.name)
      if (blockReason) {
        setValue('appointment_time', '')
      } else if (selectedProfessional) {
        fetchBusySlots(appointmentDate, selectedProfessional.name)
      }
    }
  }, [watch('appointment_date'), selectedProfessional, holidays])


  const nextStep = async () => {
    let fields = []
    if (step === 1) fields = ['patient_dni']
    else if (step === 2) fields = ['study_types']
    else if (step === 3) fields = ['appointment_date', 'appointment_time']
    else if (step === 4) {
      fields = ['patient_firstname', 'patient_lastname', 'patient_email', 'patient_email_confirm', 'patient_phone', 'patient_birthdate', 'address', 'city', 'province', 'country']
      if (!portalSettings || portalSettings.portal_show_insurance === 'true') {
        fields.push('insurance_provider')
      }
    }
    
    const isValid = await trigger(fields)

    if (step === 2 && isValid) {
      setStep(4)
      return
    }
    if (step === 1 && isValid) {
      const dni = watch('patient_dni').trim()
      setIsValidatingDni(true)
      try {
        // Search securely using RPC to bypass RLS exposure while retaining functionality
        const { data: patientData, error: rpcError } = await supabase
          .rpc('buscar_datos_paciente_por_dni', { search_dni: dni })
          .maybeSingle()

        if (rpcError) throw rpcError

        if (patientData) {
          const nameParts = parseFullName(patientData.full_name)
          setValue('patient_firstname', nameParts.firstname)
          setValue('patient_lastname', nameParts.lastname)
          setValue('patient_birthdate', patientData.birthdate || '')
          setValue('patient_email', patientData.email || '')
          setValue('patient_email_confirm', patientData.email || '')

          if (patientData.phone) {
            let phoneVal = String(patientData.phone).trim()
            // Check country code prefix
            const prefixes = ['+54', '+598', '+56', '+55', '+34']
            const foundPrefix = prefixes.find(p => phoneVal.startsWith(p))
            if (foundPrefix) {
              setValue('country_code', foundPrefix)
              setValue('patient_phone', phoneVal.slice(foundPrefix.length))
            } else {
              setValue('patient_phone', phoneVal)
            }
          }

          setValue('insurance_provider', patientData.insurance_provider || 'PARTICULAR')
          setValue('insurance_plan', patientData.insurance_plan || '')
          setValue('affiliate_number', patientData.affiliate_number || '')
          setValue('address', patientData.address || '')
          setValue('city', patientData.city || '')
          setValue('province', patientData.province || '')
          setValue('country', patientData.country || 'Argentina')

          addNotification({
            title: '¡Paciente Encontrado!',
            message: `Hola de nuevo, ${patientData.full_name}. Hemos completado tus datos personales y cobertura.`,
            type: 'success'
          })
        }
      } catch (err) {
        console.error('DNI validation and auto-fill error:', err)
      } finally {
        setIsValidatingDni(false)
      }
    }
    
    if (step === 4 && isValid) {
      if (watch('patient_email') !== watch('patient_email_confirm')) {
        alert('Los correos electrónicos no coinciden.')
        return
      }
    }

    if (isValid) setStep(step + 1)
  }

  const prevStep = () => {
    if (step === 4) setStep(2)
    else setStep(step - 1)
  }

  const handleFileChangeForStudy = (e, studyName) => {
    const files = Array.from(e.target.files).map(file => ({
      file,
      studyName
    }))
    setAttachedFiles(prev => [...prev, ...files])
  }

  const removeFile = (fileToRemove) => {
    setAttachedFiles(prev => prev.filter(item => item.file !== fileToRemove))
  }

  const handleWhatsAppConfirm = () => {
    if (!submittedData) return
    const text = `Hola GDT! Soy *${submittedData.patient_name}*, acabo de reservar mi turno para *${submittedData.study_type}* el día *${(submittedData?.appointments?.[0]?.date || '').split('-').reverse().join('/')}* a las *${(submittedData?.appointments?.[0]?.time || '00:00:00')} hs*. ¡Muchas gracias!`
    const encodedText = encodeURIComponent(text)
    window.open(`https://wa.me/5491125565076?text=${encodedText}`, '_blank')
  }

      const onInvalid = (errors) => {
        console.error('Validation Errors:', errors);
        const errorFields = Object.keys(errors).map(field => {
          if (field === 'patient_dni') return 'DNI';
          if (field === 'patient_lastname') return 'Apellido';
          if (field === 'patient_firstname') return 'Nombre';
          if (field === 'patient_email') return 'Email';
          if (field === 'patient_phone') return 'Teléfono';
          if (field === 'insurance_provider') return 'Obra Social';
          return field;
        });
        
        addNotification({ 
          title: 'Datos Incompletos', 
          message: `Por favor complete los siguientes campos obligatorios: ${errorFields.join(', ')}`, 
          type: 'error' 
        });
      };

      const handleAddToCart = async () => {
        // Validate Step 4
        let fieldsToValidate = ['patient_firstname', 'patient_lastname', 'patient_email', 'patient_email_confirm', 'patient_phone', 'patient_birthdate', 'address', 'city', 'province', 'country'];
        if (!portalSettings || portalSettings.portal_show_insurance === 'true') {
          fieldsToValidate.push('insurance_provider');
        }
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) return;

        const data = getValues();
        const studiesSelected = services.filter(s => Array.isArray(data.study_types) ? data.study_types.includes(s.name) : data.study_types === s.name);
        const studyTypeStr = studiesSelected.map(s => s.name).join(', ') || 'Consulta';
        const studyProf = resolveProfessionalForService(studyTypeStr, professionals, services);

        const cartItem = {
          ...data,
          studyTypeStr,
          studiesSelected,
          studyProf,
          files: [...attachedFiles]
        };

        setCart(prev => [...prev, cartItem]);

        // Reset step 2 and 3 fields for the next appointment
        setValue('study_types', []);
        setValue('appointment_date', '');
        setValue('appointment_time', '');
        setAttachedFiles([]);
        setStep(2); // Go back to Study Selection
        
        addNotification({ title: 'Turno encolado', message: 'Seleccione su siguiente estudio y horario.', type: 'success' });
      };

      const onSubmit = async (data) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        console.log('onSubmit start');
        
        try {
          addNotification({ title: 'Procesando...', message: 'Finalizando reservas...', type: 'info' });

          const studiesSelectedLast = services.filter(s => Array.isArray(data.study_types) ? data.study_types.includes(s.name) : data.study_types === s.name);
          const studyTypeStrLast = studiesSelectedLast.map(s => s.name).join(', ') || 'Consulta';
          const studyProfLast = resolveProfessionalForService(studyTypeStrLast, professionals, services);
          
          const lastCartItem = {
            ...data,
            studyTypeStr: studyTypeStrLast,
            studiesSelected: studiesSelectedLast,
            studyProf: studyProfLast,
            files: [...attachedFiles]
          };

          const allItems = [...cart, lastCartItem];
          let allUploadedFilesMetadata = [];
          let emailStudiesHtml = '';

          console.log('Processing', allItems.length, 'appointments');
          // Loop and insert each appointment
          for (const item of allItems) {
            console.log('Processing item', item.studyTypeStr, 'date', item.appointment_date);
            // Upload files for this item
            const itemFilesMetadata = [];
            console.log('Starting parallel file upload');
            const uploadPromises = item.files.map(async (fileObj) => {
              const file = fileObj.file;
              try {
                console.log('Uploading file', file.name);
                const fileExt = file.name.split('.').pop();
                const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
                const filePath = `${fileName}`;

                const { error: uploadError } = await supabase.storage.from('patient-docs').upload(filePath, file);
                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(filePath);
                console.log('File uploaded, public URL:', publicUrl);

                return { name: file.name, url: publicUrl, study_name: fileObj.studyName, success: true };
              } catch (err) {
                console.error('File Upload Error for', fileObj.file.name, err);
                return { success: false };
              }
            });
            
            const uploadResults = await Promise.all(uploadPromises);
            uploadResults.forEach(res => {
              if (res.success) {
                itemFilesMetadata.push({ name: res.name, url: res.url, study_name: res.study_name });
                allUploadedFilesMetadata.push({ name: res.name, url: res.url, study_name: res.study_name });
              }
            });

            console.log('Inserting DB record for appointment');
            // Insert DB
            const { error: dbError } = await supabase.from('appointments').insert([{
              patient_dni: item.patient_dni || null,
              patient_name: `${item.patient_lastname} ${item.patient_firstname}`,
              patient_phone: `${item.country_code || ''}${item.patient_phone || ''}`,
              patient_email: item.patient_email || '',
              appointment_date: item.appointment_date,
              appointment_time: item.appointment_time || '00:00:00',
              professional_name: item.studyProf?.name || professionals[0]?.name,
              study_type: item.studyTypeStr,
              requester_name: item.is_third_party === 'true' ? item.requester_name : null,
              requester_phone: item.is_third_party === 'true' ? item.requester_phone : null,
              study_details: {
                patient_firstname: item.patient_firstname,
                patient_lastname: item.patient_lastname,
                patient_dni: item.patient_dni,
                patient_birthdate: item.patient_birthdate,
                insurance_provider: item.insurance_provider || 'PARTICULAR',
                insurance_plan: item.insurance_plan || '',
                affiliate_number: item.affiliate_number || '',
                patient_type: 'Ambulatorio',
                address: item.address || '',
                city: item.city || '',
                province: item.province || '',
                country: item.country || 'Argentina',
                reason: item.reason || '',
                branch_name: selectedBranch?.name || branches[0]?.name,
                fasting: item.fasting || false,
                studies_count: item.studiesSelected.length || 1,
                attached_files: itemFilesMetadata,
                attached_files_count: itemFilesMetadata.length,
                professional_id: item.studyProf?.id,
                professional_name: item.studyProf?.name
              },
              status: 'pendiente'
            }]);

            console.log('DB insert completed', dbError ? 'with error' : 'success');
            if (dbError) throw dbError;

            console.log('Inserting notification for item');
            // Notification for each
            await supabase.from('notifications').insert([{
              title: 'Nuevo Turno Online',
              description: `El paciente ${item.patient_lastname} solicitó turno para: ${item.studyTypeStr} el ${item.appointment_date} a las ${item.appointment_time} hs.`,  
              type: 'appointment',
              read: false
            }]);

            // Append to email HTML
            emailStudiesHtml += `
              <li style="margin-bottom: 10px;">
                <strong>${item.studyTypeStr}</strong><br/>
                🗓 ${item.appointment_date} a las ⏰ ${item.appointment_time ? item.appointment_time : 'A Coordinar'} hs<br/>
                <em>Profesional: ${item.studyProf?.name || 'A asignar'}</em>
              </li>
            `;
          }

          // 4. Send Email (Combined)
          const filesLinksHtml = allUploadedFilesMetadata.map(f => 
            `<li><a href="${f.url}" style="color: #1a73e8; font-weight: bold; text-decoration: none;">📄 Ver ${f.name}</a></li>`
          ).join('');

          try {
            console.log('Invoking email function');
// Generate PDF
          let pdfBase64 = null;
          try {
            const { jsPDF } = await import('jspdf');
            const doc = new jsPDF();
            doc.setFontSize(16);
            doc.text('Reserva de Turnos (Portal)', 20, 20);
            
            doc.setFontSize(12);
            let pdfY = 35;
            doc.text(`Paciente: ${data.patient_lastname}, ${data.patient_firstname}`, 20, pdfY); pdfY += 10;
            doc.text(`DNI: ${data.patient_dni}`, 20, pdfY); pdfY += 10;
            doc.text(`Telefono: ${data.country_code || ''} ${data.patient_phone || ''}`, 20, pdfY); pdfY += 10;
            doc.text(`Email: ${data.patient_email || 'No provisto'}`, 20, pdfY); pdfY += 10;
            doc.text(`Obra Social: ${data.insurance_provider || 'PARTICULAR'}`, 20, pdfY); pdfY += 10;
            doc.text(`Plan: ${data.insurance_plan || 'N/A'} - Afiliado: ${data.affiliate_number || 'N/A'}`, 20, pdfY); pdfY += 10;
            
            if (data.is_third_party === 'true') {
              doc.text(`Solicitado por: ${data.requester_name || 'Tercero'} (Tel: ${data.requester_phone || 'N/A'})`, 20, pdfY);
              pdfY += 10;
            }

            const reasonLines = doc.splitTextToSize(`Observaciones: ${data.reason || 'Ninguna'}`, 170);
            doc.text(reasonLines, 20, pdfY);
            
            let y = pdfY + (reasonLines.length * 7) + 10;
            
            doc.setFontSize(14);
            doc.text(`Estudios Solicitados (${allItems.length}):`, 20, y);
            y += 10;
            doc.setFontSize(12);
            
            allItems.forEach((item, idx) => {
              const text = `${idx + 1}. ${item.studyTypeStr} - ${item.appointment_date} ${item.appointment_time ? item.appointment_time + 'hs' : 'A Coordinar'}`;
              doc.text(text, 20, y);
              y += 7;
            });
            
            pdfBase64 = doc.output('datauristring').split(',')[1];
          } catch(e) {
            console.error('PDF Error:', e);
          }

          const attachments = [];
          if (pdfBase64) {
            attachments.push({
              filename: `Reserva_${data.patient_lastname}.pdf`,
              content: pdfBase64
            });
          }

          const { data: res, error: funcError } = await supabase.functions.invoke('resend', {
              body: {
                to: 'info@gestionamientodeturnos.com',
                subject: `GDT: NUEVOS TURNOS - ${data.patient_lastname} ${data.patient_firstname}`,
                reply_to: data.patient_email,
                attachments: attachments,
                html: `
                  <div style="font-family: sans-serif; padding: 20px; color: #111; max-width: 600px; background: #ffffff;">
                    <h2 style="color: #0088cc; border-bottom: 2px solid #0088cc; padding-bottom: 10px;">Reserva de Turnos (Portal)</h2>
                    <p><strong>Paciente:</strong> ${data.patient_lastname}, ${data.patient_firstname}</p>
                    <p><strong>DNI:</strong> ${data.patient_dni}</p>
                    <p><strong>Teléfono:</strong> ${data.country_code || ''} ${data.patient_phone || ''}</p>
                    <p><strong>Email:</strong> ${data.patient_email || 'No provisto'}</p>
                    <p><strong>Obra Social:</strong> ${data.insurance_provider || 'PARTICULAR'}</p>
                    <p><strong>Plan / Nº Afiliado:</strong> ${data.insurance_plan || 'N/A'} / ${data.affiliate_number || 'N/A'}</p>
                    ${data.is_third_party === 'true' ? `<div style="background: #e3f2fd; padding: 10px; border-left: 4px solid #2196f3; margin: 15px 0;">
                      <strong>ATENCIÓN: TURNO SOLICITADO POR UN TERCERO</strong><br/>
                      <strong>Nombre del solicitante:</strong> ${data.requester_name}<br/>
                      <strong>Teléfono del solicitante:</strong> ${data.requester_phone}
                    </div>` : ''}
                    <div style="background: #fff3cd; padding: 10px; border-left: 4px solid #ffc107; margin: 15px 0;">
                      <strong>Mensaje / Observaciones:</strong><br/>
                      ${data.reason ? data.reason.replace(/\n/g, '<br/>') : '<em>Sin observaciones</em>'}
                    </div>
                    <h3 style="margin-top: 20px;">Estudios Solicitados (${allItems.length}):</h3>
                    <ul>
                      ${emailStudiesHtml}
                    </ul>
                    <div style="background: #f4f4f4; padding: 15px; border-radius: 8px; margin-top: 20px;">
                      <h3 style="margin-top: 0;">Links a Documentos Adjuntos (${allUploadedFilesMetadata.length})</h3>
                      ${allUploadedFilesMetadata.length > 0 ? `<ul>${filesLinksHtml}</ul>` : '<p>Sin archivos adjuntos.</p>'}
                    </div>
                  </div>
                `
              }
            });

            if (funcError) console.error('Email Dispatch Note:', funcError);
            console.log('Email function completed');
          } catch (emailErr) {
            console.error('Email service currently unavailable:', emailErr);
          }

          setSubmittedData({
            patient_name: `${data.patient_lastname} ${data.patient_firstname}`,
            appointments: allItems.map(i => ({ date: i.appointment_date, time: i.appointment_time, study: i.studyTypeStr }))
          });
          setSubmitted(true);
          setCart([]);
          console.log('onSubmit completed successfully');
          setStep(6);
        } catch (error) {
          console.error('Finalize Error:', error);
          addNotification({ 
            title: 'Error al finalizar', 
            message: error.message || 'Hubo un error al procesar la reserva.', 
            type: 'error' 
          });
        } finally {
          setIsSubmitting(false);
          console.log('onSubmit final finally block executed');
        }
      };

  if (isPortalSettingsLoading) {
    return (
      <div className="patient-portal-wrapper" translate="no" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#cbd5e1' }}>
          <Loader2 className="animate-spin" style={{ color: 'var(--primary-cyan)' }} />
          <span>Cargando portal...</span>
        </div>
      </div>
    )
  }

  if (portalSettings && portalSettings.portal_enabled === 'false') {
    return (
      <div className="patient-portal-wrapper" translate="no">
        <div className="portal-container-compact">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="glass-card error-card" style={{ border: '1px solid rgba(255, 82, 82, 0.3)', textAlign: 'center', padding: '3rem 2rem' }}>
            <div className="success-icon-wrapper" style={{ background: 'rgba(255, 82, 82, 0.1)', color: '#ff5252', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem auto' }}>
              <AlertCircle size={48} strokeWidth={1.5} />
            </div>
            <h2>Portal Deshabilitado</h2>
            <p className="w-300" style={{ margin: '1.5rem 0', color: 'var(--text-primary)' }}>
              El portal de autogestión de turnos de **GDT** se encuentra temporalmente fuera de servicio o deshabilitado.
            </p>
            <p className="w-300" style={{ fontSize: '0.95rem', opacity: 0.7, marginBottom: '2.5rem', color: 'var(--text-muted)' }}>
              Si necesita reservar, reprogramar o cancelar una cita, por favor comuníquese por teléfono al {portalSettings.portal_phone || '1125565076'} o vía WhatsApp al {portalSettings.portal_whatsapp || '5491112345678'}.
            </p>
            {portalSettings.portal_whatsapp && (
              <div className="success-actions" style={{ display: 'flex', justifyContent: 'center' }}>
                <a 
                  href={`https://wa.me/${portalSettings.portal_whatsapp}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-portal-next"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#25d366', color: 'white', padding: '0.8rem 1.8rem', borderRadius: 'var(--radius-full)', fontWeight: 'bold' }}
                >
                  <MessageSquare size={18} /> Contactar por WhatsApp
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="patient-portal-wrapper" translate="no">
        <div className="portal-container-compact">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="glass-card success-card">
            <div className="success-icon-wrapper">
              <CheckCircle2 size={48} strokeWidth={1.5} color="#00e5ff" />
            </div>
            <h2 className="w-200">Solicitud Recibida</h2>
            <div className="success-card">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h2>¡Solicitud Enviada con Éxito!</h2>
              <p>Tu solicitud ha sido procesada correctamente.</p>
              
              <div className="success-details">
                <p><strong>Paciente:</strong> {submittedData?.patient_name}</p>
                <p><strong>Turnos solicitados ({submittedData?.appointments?.length}):</strong></p>
                <ul style={{ textAlign: 'left', paddingLeft: '20px', background: 'rgba(0,0,0,0.05)', padding: '15px', borderRadius: '8px' }}>
                  {submittedData?.appointments?.map((app, idx) => (
                    <li key={idx} style={{ marginBottom: '8px' }}>
                      <strong>{app.study}</strong> - {(app.date || '').split('-').reverse().join('/')} a las {app.time} hs
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="success-actions">
              <button onClick={() => window.location.reload()} className="btn-portal-next" style={{ maxWidth: '320px' }}>
                Entendido y Finalizar
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="patient-portal-wrapper" translate="no">
      <div className="portal-container">
        {/* HEADER INFO BAR WITH NOTICE */}
        <div className="header-info-bar" style={{ display: 'flex', flexWrap: 'wrap', width: '100%', margin: '0 0 2rem 0', padding: '1.5rem', background: 'rgba(255, 255, 255, 0.02)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-glass)', gap: '0' }}>
          
          {/* LEFT 50% - MESSAGE */}
          <div style={{ flex: '1 1 50%', minWidth: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 1.5rem' }}>
            <div style={{ background: 'rgba(0, 229, 255, 0.03)', border: '1px solid rgba(0, 229, 255, 0.15)', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', maxWidth: '100%' }}>
              <AlertCircle size={24} style={{ color: '#00e5ff', flexShrink: 0 }} />
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6', margin: 0, fontWeight: '400', textAlign: 'left' }}>
                Los horarios de los turnos están en revisión de disponibilidad. Mientras se realiza la revisión, los turnos quedan pendientes. <strong style={{ color: 'var(--primary-cyan)', fontWeight: '600' }}>Los turnos confirmados serán otorgados por email.</strong> Apenas se termine la revisión, se le notificará la respuesta.
              </p>
            </div>
          </div>

          {/* RIGHT 50% - CONTACT */}
          <div style={{ flex: '1 1 50%', minWidth: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3rem', padding: '1.5rem', borderLeft: '1px solid var(--border-glass)' }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
              <div className="info-icon" style={{ margin: '0 auto', width: '48px', height: '48px' }}><Mail size={22} /></div>
              <div>
                <h5 style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', marginBottom: '6px', marginTop: 0 }}>Email</h5>
                <p style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>{portalSettings?.portal_email || 'info@gestionamientodeturnos.com'}</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
              <div className="info-icon" style={{ margin: '0 auto', width: '48px', height: '48px' }}><Phone size={22} /></div>
              <div>
                <h5 style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', marginBottom: '6px', marginTop: 0 }}>Teléfono</h5>
                <p style={{ fontSize: '0.9rem', fontWeight: '500', margin: 0 }}>{portalSettings?.portal_phone || '1125565076'}</p>
              </div>
            </div>

          </div>
        </div>

        <div className="portal-layout">
          {/* LEFT COLUMN: FORM */}
          <div className="form-column">
            <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <React.Fragment>
                {step === 1 && (
                  <motion.div key="step1" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="glass-card">
                    <div className="step-title-area">
                      <div className="step-badge">Paso 01</div>
                      <h2>Identificación</h2>
                      <p>Ingrese su documento para comenzar la reserva</p>
                    </div>

                    <div className="form-content-vertical">
                      {portalSettings && portalSettings.portal_booking_enabled === 'false' && (
                        <div className="blocked-day-warning" style={{ marginBottom: '1.5rem', borderColor: 'rgba(255, 184, 0, 0.3)', background: 'rgba(255, 184, 0, 0.05)', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: '12px', border: '1px solid' }}>
                          <AlertCircle size={20} style={{ color: '#ffb800', flexShrink: 0 }} />
                          <div className="warning-text" style={{ textAlign: 'left' }}>
                            <h4 style={{ color: '#ffb800', margin: 0, fontSize: '0.9rem', fontWeight: 'bold' }}>Toma de Turnos Suspendida</h4>
                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#cbd5e1' }}>La reserva de turnos online se encuentra desactivada temporalmente.</p>
                          </div>
                        </div>
                      )}

                      <div className="input-group center-input">
                        <label><Hash size={14} /> Nº de Documento (DNI)</label>
                        <input {...register('patient_dni', { required: true })} placeholder="35.123.456" className="large-input" />
                      </div>
                      <div className="step-actions">
                        <div></div>
                        <button 
                          type="button" 
                          onClick={nextStep} 
                          disabled={!watch('patient_dni') || isValidatingDni || (portalSettings && portalSettings.portal_booking_enabled === 'false')} 
                          className="btn-portal-next"
                        >
                          {isValidatingDni ? (
                            <>
                              <Loader2 className="animate-spin" size={18} style={{ marginRight: '8px' }} />
                              Validando...
                            </>
                          ) : (
                            <>
                              Validar y Continuar <ArrowRight size={18} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="step2" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }}>
                    <div className="step-title-area">
                      <div className="step-badge">Paso 02</div>
                      <h2>Estudios y Prácticas</h2>
                      <p>Seleccione el estudio o práctica médica requerida (puede seleccionar múltiples)</p>
                    </div>
                    
                    {portalSettings && portalSettings.portal_show_specialty === 'true' && (
                      <div className="filter-specialty-container" style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                        <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', textAlign: 'left', fontWeight: 'bold' }}>
                          Filtrar por Especialidad
                        </label>
                        <select 
                          value={selectedSpecialty} 
                          onChange={(e) => setSelectedSpecialty(e.target.value)}
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '12px', padding: '1rem', color: 'white', width: '100%', outline: 'none', fontSize: '0.95rem' }}
                        >
                          <option value="ALL">-- TODAS LAS ESPECIALIDADES --</option>
                          {availableSpecialties.map(spec => (
                            <option key={spec} value={spec}>{spec.toUpperCase()}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="staff-grid">
                      {filteredServicesList.map((service) => {
                        const isSelected = watch('study_types')?.includes(service.name);
                        return (
                          <div key={service.id} 
                            className={`staff-card ${isSelected ? 'active' : ''}`}
                            onClick={() => {
                              const current = watch('study_types') || []
                              let next = []
                              if (current.includes(service.name)) {
                                next = current.filter(c => c !== service.name)
                                // Also clean up files for this study if unselected
                                setAttachedFiles(prev => prev.filter(f => f.studyName !== service.name))
                              } else {
                                next = [...current, service.name]
                              }
                              setValue('study_types', next)
                              
                              // Automatically update matching professional behind the scenes
                              updateProfessionalForServices(next, professionals, services)
                            }}
                          >
                            <div className="staff-info">
                              <h4>{service.name}</h4>
                              <p className="specialty">⏱ {service.duration} min {service.price ? `| $ ${parseFloat(service.price).toLocaleString('es-AR')}` : ''}</p>
                              {service.requirements && (
                                <p className="study-requirements-hint" style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '5px' }}>
                                  📋 {service.requirements}
                                </p>
                              )}
                            </div>
                            
                            <button type="button" className="btn-card-action">
                              {isSelected ? 'Seleccionado' : 'Seleccionar'}
                            </button>

                            {/* INLINE PREMIUM FILE UPLOAD FOR SELECTED STUDY */}
                            <React.Fragment>
                              {isSelected && (
                                <motion.div 
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="study-inline-upload"
                                  onClick={(e) => e.stopPropagation()} // Prevent toggling selection when clicking inside upload area
                                >
                                  <div className="inline-upload-divider"></div>
                                  <label className="inline-upload-label">
                                    <Upload size={12} /> Adjuntar Orden Médica
                                  </label>
                                  
                                  <div className="inline-upload-dropzone">
                                    <input 
                                      type="file" 
                                      multiple 
                                      id={`inline-file-upload-${service.id}`} 
                                      onChange={(e) => handleFileChangeForStudy(e, service.name)} 
                                      hidden 
                                    />
                                    <label htmlFor={`inline-file-upload-${service.id}`} className="inline-upload-trigger-btn">
                                      <Upload size={14} />
                                      <span>Seleccionar archivo</span>
                                    </label>
                                  </div>

                                  {attachedFiles.filter(f => f.studyName === service.name).length > 0 && (
                                    <div className="inline-attached-files-list">
                                      {attachedFiles.filter(f => f.studyName === service.name).map((item, idx) => (
                                        <div key={idx} className="inline-file-item">
                                          <FileText size={12} className="inline-file-icon" />
                                          <span className="inline-file-name" title={item.file.name}>{item.file.name}</span>
                                          <button 
                                            type="button" 
                                            className="inline-file-remove" 
                                            onClick={(e) => { e.stopPropagation(); removeFile(item.file); }}
                                          >
                                            <X size={12} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </React.Fragment>
                          </div>
                        );
                      })}
                    </div>

                    <div className="step-actions mt-3" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <button type="button" onClick={prevStep} className="btn-portal-back"><ArrowLeft size={18} /> <span>Volver</span></button>
                      <button type="button" onClick={nextStep} disabled={!watch('study_types') || watch('study_types').length === 0} className="btn-portal-next">
                        <span>Continuar al Calendario</span> <ArrowRight size={18} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="step3" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="glass-card">
                    <div className="step-title-area">
                      <div className="step-badge">Paso 03</div>
                      <h2>Disponibilidad</h2>
                      <p>Seleccione el día y horario que mejor le convenga</p>
                    </div>
                    
                    <div className="form-content-vertical">
                      <div className="input-group">
                        <label><Calendar size={14} /> Fecha del Turno</label>
                        <input type="date" {...register('appointment_date', { required: true })} min={getLocalISODate(new Date())} />
                      </div>
                      
                      {blockReason ? (
                        <div className="blocked-day-warning">
                          <AlertCircle size={24} className="warning-icon" />
                          <div className="warning-text">
                            <h4>Agenda Bloqueada / Día No Laborable</h4>
                            <p>{blockReason}</p>
                            <small>La especialidad o el profesional seleccionado no atiende este día. Por favor, seleccione otra fecha en el calendario.</small>
                          </div>
                        </div>
                      ) : (
                        <div className="slots-area">
                          <label>Horarios Disponibles</label>
                          {isSlotsLoading ? (
                            <div className="slots-loader"><Loader2 className="animate-spin" /></div>
                          ) : (
                            <div className="slots-grid">
                              {generateTimeSlots().map(time => (
                                <button key={time} type="button" disabled={busySlots.includes(time)}
                                  onClick={() => setValue('appointment_time', time)}
                                  className={`slot-btn ${watch('appointment_time') === time ? 'selected' : ''}`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          )}
                          <input type="hidden" {...register('appointment_time', { required: true })} />
                        </div>
                      )}

                      <div className="step-actions">
                        <button type="button" onClick={prevStep} className="btn-portal-back"><ArrowLeft size={18} /> Volver</button>
                        <button type="button" onClick={nextStep} disabled={!watch('appointment_time') || !!blockReason} className="btn-portal-next">Continuar <ArrowRight size={18} /></button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div key="step4" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="glass-card">
                    <div className="step-title-area">
                      <div className="step-badge">Paso 04</div>
                      <h2>Datos de Contacto</h2>
                      <p>Complete su información para finalizar la reserva</p>
                    </div>

                    <div className="form-content-vertical">
                      <div className="input-group" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                        <label style={{ fontSize: '0.9rem', marginBottom: '0.75rem', color: 'var(--text-bright)', display: 'block' }}>¿Quién solicita el turno?</label>
                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0, color: 'var(--text-muted)' }}>
                            <input type="radio" value="false" {...register('is_third_party')} /> El propio paciente
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0, color: 'var(--text-muted)' }}>
                            <input type="radio" value="true" {...register('is_third_party')} /> Un tercero (Familiar, Empresa, etc)
                          </label>
                        </div>
                      </div>

                      {watch('is_third_party') === 'true' && (
                        <div className="input-row" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(0, 229, 255, 0.05)', borderRadius: '12px', border: '1px solid rgba(0, 229, 255, 0.2)' }}>
                          <div className="input-group">
                            <label style={{ color: 'var(--primary-cyan)' }}>Nombre de quien solicita</label>
                            <input {...register('requester_name', { required: watch('is_third_party') === 'true' })} placeholder="Ej: Recursos Humanos, Juan Pérez" />
                          </div>
                          <div className="input-group">
                            <label style={{ color: 'var(--primary-cyan)' }}>Teléfono de contacto directo</label>
                            <input {...register('requester_phone', { required: watch('is_third_party') === 'true' })} placeholder="Teléfono de quien solicita" />
                          </div>
                        </div>
                      )}

                      <h3 style={{ fontSize: '1rem', marginBottom: '1rem', marginTop: '1rem', color: 'var(--text-bright)' }}>Datos del Paciente</h3>

                      <div className="input-row">
                        <div className="input-group">
                          <label>Apellido</label>
                          <input {...register('patient_lastname', { required: true, validate: v => v.trim().length > 0 })} placeholder="Pérez" />
                        </div>
                        <div className="input-group">
                          <label>Nombre</label>
                          <input {...register('patient_firstname', { required: true, validate: v => v.trim().length > 0 })} placeholder="Juan" />
                        </div>
                      </div>

                      <div className="input-row">
                        <div className="input-group">
                          <label>Fecha de Nacimiento</label>
                          <input type="date" {...register('patient_birthdate', { required: true })} />
                        </div>
                        <div className="input-group">
                          <label>Peso del paciente (Opcional)</label>
                          <input type="text" {...register('patient_weight')} placeholder="Ej: 75 kg" />
                        </div>
                      </div>

                      <div className="input-row">
                        <div className="input-group">
                          <label>Email <span style={{fontSize: "0.85rem", color: "var(--text-muted)", fontWeight: "normal"}}>(Aquí recibirás la respuesta)</span></label>
                          <input type="email" {...register('patient_email', { required: true, validate: v => v.trim().length > 0 })} placeholder="ejemplo@correo.com" />
                        </div>
                        <div className="input-group">
                          <label>Repite tu Email</label>
                          <input type="email" {...register('patient_email_confirm', { required: true })} placeholder="Repite tu correo..." />
                        </div>
                      </div>

                      <div className="input-group">
                        <label>Teléfono / WhatsApp</label>
                        <div className="phone-input-row">
                          <div className={`custom-select country-select ${isCountryOpen ? 'open' : ''}`} onClick={() => setIsCountryOpen(!isCountryOpen)}>
                            <span>{watch('country_code') || '+54'}</span>
                            <ChevronDown size={14} />
                            {isCountryOpen && (
                              <div className="select-options">
                                {[
                                  { code: '+54', label: '🇦🇷 +54' },
                                  { code: '+598', label: '🇺🇾 +598' },
                                  { code: '+56', label: '🇨🇱 +56' },
                                  { code: '+55', label: '🇧🇷 +55' },
                                  { code: '+34', label: '🇪🇸 +34' }
                                ].map(c => (
                                  <div key={c.code} className="option" onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setValue('country_code', c.code); 
                                    setIsCountryOpen(false); 
                                  }}>{c.label}</div>
                                ))}
                              </div>
                            )}
                          </div>
                          <input type="hidden" {...register('country_code')} defaultValue="+54" />
                          <input {...register('patient_phone', { required: true })} placeholder="11 2345 6789" className="flex-1" />
                        </div>
                      </div>
                      <div className="input-group">
                        <label>Dirección Física</label>
                        <input {...register('address', { required: true })} placeholder="Calle, Número, Piso, Depto..." />
                      </div>

                      <div className="input-row">
                        <div className="input-group">
                          <label>Ciudad</label>
                          <input {...register('city', { required: true })} placeholder="Ej: CABA" />
                        </div>
                        <div className="input-group">
                          <label>Provincia</label>
                          <input {...register('province', { required: true })} placeholder="Ej: Buenos Aires" />
                        </div>
                      </div>

                      <div className="input-group">
                        <label>País</label>
                        <input {...register('country', { required: true })} defaultValue="Argentina" placeholder="Ej: Argentina" />
                      </div>

                      {(!portalSettings || portalSettings.portal_show_insurance === 'true') && (
                        <div className="input-row">
                          <div className="input-group">
                            <label>Obra Social / Seguro Médico</label>
                            <input 
                              type="text" 
                              placeholder="Ej: OSDE, Swiss Medical, Particular..." 
                              {...register('insurance_provider', { required: true })} 
                            />
                          </div>
                          <div className="input-group">
                            <label>Nº de Afiliado / Credencial</label>
                            <input {...register('affiliate_number')} placeholder="Ej: 123456789" />
                          </div>
                        </div>
                      )}

                      <div className="input-group">
                        <label>Motivo del turno (Opcional)</label>
                        <textarea {...register('reason')} placeholder="Escriba aquí sus síntomas o motivo..." rows="3" />
                      </div>

                      {/* FILE ATTACHMENT SECTION */}
                      <div className="file-attachment-section-container">
                        <h3 className="attachment-title-main">📁 Adjuntar Documentación del Turno</h3>
                        
                        {/* Render general upload area */}
                        <div className="file-attachment-section general-upload">
                          <label className="section-label">
                            <Upload size={14} /> Credencial de Cobertura / DNI / Otros Archivos
                          </label>
                          <div className="upload-area">
                            <input 
                              type="file" 
                              multiple 
                              id="file-upload-general" 
                              onChange={(e) => handleFileChangeForStudy(e, 'General')} 
                              hidden 
                            />
                            <label htmlFor="file-upload-general" className="upload-trigger">
                              <Upload size={24} />
                              <span>Subir Credencial o Archivos Generales</span>
                              <small>Formatos: PDF, JPG, PNG. Máx 5MB por archivo.</small>
                            </label>
                          </div>
                          
                          {attachedFiles.filter(f => f.studyName === 'General').length > 0 && (
                            <div className="attached-files-list">
                              {attachedFiles.filter(f => f.studyName === 'General').map((item, idx) => (
                                <div key={idx} className="file-item">
                                  <FileText size={14} />
                                  <span className="file-name">{item.file.name}</span>
                                  <button type="button" onClick={() => removeFile(item.file)}><X size={14} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                      </div>

                      <div className="cart-info-box" style={{ background: 'rgba(0, 229, 255, 0.05)', border: '1px solid rgba(0, 229, 255, 0.2)', padding: '15px', borderRadius: '12px', marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                        <div style={{ color: 'var(--primary-cyan)', marginTop: '2px' }}>
                          <Info size={24} />
                        </div>
                        <div>
                          <h4 style={{ margin: '0 0 5px 0', fontSize: '0.95rem', color: 'var(--text-bright)' }}>¿Necesita más de un turno?</h4>
                          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                            Si tiene varias órdenes médicas o necesita otro estudio adicional, presione <strong>"AGREGAR OTRO TURNO"</strong>. Puede sumar todos los turnos que necesite y finalizar su reserva de una sola vez.
                          </p>
                        </div>
                      </div>

                      <div className="step-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button type="button" onClick={prevStep} className="btn-portal-back" style={{ flex: 1 }}><ArrowLeft size={18} /> Volver</button>
                          <button type="button" onClick={handleAddToCart} disabled={isSubmitting} className="btn-portal-secondary" style={{ flex: 2, background: 'var(--bg-glass)', border: '1px solid var(--border-glass)', color: 'var(--text-primary)', padding: '1rem', borderRadius: '12px', fontWeight: 'bold' }}>
                            <Plus size={18} style={{ display: 'inline', marginRight: '5px' }} /> AGREGAR OTRO TURNO
                          </button>
                        </div>
                        <button type="submit" disabled={isSubmitting} className="btn-portal-submit" style={{ width: '100%' }}>
                          {isSubmitting ? <Loader2 className="animate-spin" /> : 'FINALIZAR RESERVA'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </React.Fragment>
            </form>
          </div>

          {/* RIGHT COLUMN: LIVE SUMMARY */}
          <div className="summary-column">
            <div className="summary-sticky-wrapper">
              <div className="summary-card-modern">
                <div className="summary-card-header">
                  <h3>Resumen del Turno</h3>
                  <div className="pulse-dot"></div>
                </div>

                <div className="summary-steps-list">
                  {cart.length > 0 && (
                    <div className="cart-summary-section" style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '0.85rem', color: 'var(--primary-cyan)', marginBottom: '10px' }}>🛒 Turnos encolados ({cart.length}):</h4>
                      {cart.map((c, i) => (
                        <div key={i} style={{ fontSize: '0.8rem', borderLeft: '2px solid var(--primary-cyan)', paddingLeft: '8px', marginBottom: '8px' }}>
                          <strong>{c.studyTypeStr}</strong><br />
                          {(c.appointment_date || '').split('-').reverse().join('/')}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* STEP 1 SUMMARY */}
                  <div className={`summary-step-item ${step >= 1 && watch('patient_dni') ? 'completed' : ''}`}>
                    <div className="step-icon"><Hash size={16} /></div>
                    <div className="step-content">
                      <div className="step-header">
                        <span className="step-label">Identificación</span>
                        {step > 1 && <button className="edit-btn" onClick={() => setStep(1)}>Editar</button>}
                      </div>
                      {watch('patient_dni') ? (
                        <div className="step-value-box">
                          <p className="main-val">DNI: {watch('patient_dni')}</p>
                        </div>
                      ) : <p className="pending-val">Pendiente...</p>}
                    </div>
                  </div>

                  {/* STEP 2 SUMMARY */}
                  <div className={`summary-step-item ${step >= 2 && watch('study_types')?.length > 0 ? 'completed' : ''}`}>
                    <div className="step-icon"><Stethoscope size={16} /></div>
                    <div className="step-content">
                      <div className="step-header">
                        <span className="step-label">Estudios Seleccionados</span>
                        {step > 2 && <button className="edit-btn" onClick={() => setStep(2)}>Editar</button>}
                      </div>
                      {watch('study_types')?.length > 0 ? (
                        <div className="step-value-box">
                          <p className="main-val">{watch('study_types').join(', ')}</p>
                        </div>
                      ) : <p className="pending-val">Pendiente selección...</p>}
                    </div>
                  </div>

                  {/* STEP 4 SUMMARY (PREVIEW) */}
                  <div className={`summary-step-item ${step === 4 ? 'active' : ''}`}>
                    <div className="step-icon"><User size={16} /></div>
                    <div className="step-content">
                      <span className="step-label">Datos del Paciente</span>
                      {step === 4 ? (
                        <div className="step-value-box">
                          <p className="main-val">{watch('patient_lastname') || '...'} {watch('patient_firstname') || '...'}</p>
                          <p className="sub-val">
                            {(!portalSettings || portalSettings.portal_show_insurance === 'true') 
                              ? (watch('insurance_provider') || 'Particular') 
                              : 'Particular'}
                          </p>
                          {attachedFiles.length > 0 && <p className="files-val">📎 {attachedFiles.length} archivos adjuntos</p>}
                        </div>
                      ) : <p className="pending-val">Último paso...</p>}
                    </div>
                  </div>
                </div>

                <div className="summary-footer-info">
                  <ClipboardList size={14} />
                  <p>Sus datos están protegidos y encriptados bajo protocolos de seguridad GDT.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientPortal
